import { defineStore } from 'pinia'

import { StoreState } from '@shared/models/interfaces/store'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { AbortFetchRequest } from '@shared/utils/helpers/abort-fetch-request'
import {
  lsGet,
  LsGlobalKeys,
  lsSave,
} from '@shared/utils/helpers/local-storage'
import { isPending } from '@shared/utils/helpers/request-status'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { Conversation } from '@protos/apps/vc/conversation_pb'
import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'
import { UserType } from '@protos/user_type_pb'

import { setNewConversations } from '@vc/helpers/set-conversations'
import { ConversationSubTabs } from '@vc/models/enums/conversation-sub-tabs'
import { ChatSubTabs, ChatTabs } from '@vc/models/enums/tabs'
import { IConversation } from '@vc/models/proto/base/conversations'
import { ChatUsersIndexDTO } from '@vc/models/proto/dto/chat-users'
import {
  ConversationShowDTO,
  ConversationsIndexDTO,
  ConversationUpdateDTO,
  CreateConversationDTO,
  ICreateConversation,
  IUpdateDataDTO,
} from '@vc/models/proto/dto/conversations'
import { ChatUsersIndexRO } from '@vc/models/proto/ro/chat-users'
import {
  ConversationItemRO,
  ConversationsIndexRO,
} from '@vc/models/proto/ro/conversations'
import { useMessagesStore } from '@vc/store/messages'

interface State {
  selectedTab: ChatTabs
  selectedSubTab: ChatSubTabs
  conversation: StoreState<Conversation>
  bots: StoreState<ChatUsersIndexRO>
  users: StoreState<ChatUsersIndexRO>
  isLoading: boolean
  searchQuery: string
  openedConversations: StoreState<ConversationsIndexRO>
  waitingRoomConversations: StoreState<ConversationsIndexRO>
  invitedConversations: StoreState<ConversationsIndexRO>
  currentConversationId: number | null
  waitingRoomAttention: Record<ChatTabs.All | ChatTabs.Me, boolean>
  waitingRoomAttentionNoticedIds: Record<number, boolean>
  allTabAttention: boolean
}

export type ConversationsDataTypes = Pick<
  State,
  'openedConversations' | 'invitedConversations' | 'waitingRoomConversations'
>

const state = (): State => ({
  selectedTab: lsGet(LsGlobalKeys.SelectedTab, ChatTabs.Me),
  selectedSubTab: ChatSubTabs.Open,
  isLoading: false,
  conversation: getInitialState(),
  users: getInitialState(),
  bots: getInitialState(),
  searchQuery: '',
  openedConversations: getInitialState(),
  waitingRoomConversations: getInitialState(),
  invitedConversations: getInitialState(),
  currentConversationId: null,
  waitingRoomAttention: { [ChatTabs.Me]: false, [ChatTabs.All]: false },
  waitingRoomAttentionNoticedIds: {},
  allTabAttention: false,
})

export const conversationsControllers: Partial<
  Record<ChatSubTabs, AbortFetchRequest>
> = {}

export const apiTransport = new ApiTransport(resourcePath, 'vc')

export const useConversationsStore = defineStore('conversations', {
  state,

  actions: {
    async fetchOpenedConversations(payload: {
      page: number
      chatWidgetId: number
    }) {
      if (isNaN(payload?.chatWidgetId)) {
        return
      }
      if (isPending(this.openedConversations)) {
        conversationsControllers.open?.abortFetchRequest()
      }
      conversationsControllers.open = new AbortFetchRequest()
      if (payload.page === 1) {
        this.openedConversations = updateState(
          this.openedConversations,
          new ConversationsIndexRO({
            data: [],
          })
        )
      }

      this.openedConversations = updateState(this.openedConversations)

      try {
        const dto = new ConversationsIndexDTO({
          ...payload,
          perPage: 20,
          tab: this.selectedTab,
          subTab: ChatSubTabs.Open,
          search: this.searchQuery,
        })

        const data = await apiTransport.get<
          ConversationsIndexDTO,
          ConversationsIndexRO
        >(
          '/conversations',
          dto,
          ConversationsIndexRO,
          conversationsControllers.open.getInstance().signal
        )

        const { data: existingList } = this.openedConversations.data ?? {}

        this.openedConversations = updateState(
          this.openedConversations,
          new ConversationsIndexRO(
            setNewConversations(payload?.page ?? 1, existingList ?? [], data)
          )
        )
      } catch (error) {
        this.openedConversations = updateState(
          this.openedConversations,
          error as Error
        )
        throw error
      }
    },

    async fetchWaitingRoomConversations(payload: {
      page: number
      chatWidgetId: number
    }) {
      if (isNaN(payload?.chatWidgetId)) {
        return
      }
      if (isPending(this.waitingRoomConversations)) {
        conversationsControllers.waiting?.abortFetchRequest()
      }
      conversationsControllers.waiting = new AbortFetchRequest()

      if (payload.page === 1) {
        this.waitingRoomConversations = updateState(
          this.waitingRoomConversations,
          new ConversationsIndexRO({
            data: [],
          })
        )
      }

      this.waitingRoomConversations = updateState(this.waitingRoomConversations)

      try {
        const dto = new ConversationsIndexDTO({
          ...payload,
          perPage: 20,
          tab: this.selectedTab,
          subTab: ChatSubTabs.Waiting,
          search: this.searchQuery,
        })

        const data = await apiTransport.get<
          ConversationsIndexDTO,
          ConversationsIndexRO
        >(
          '/conversations',
          dto,
          ConversationsIndexRO,
          conversationsControllers.waiting.getInstance().signal
        )

        const { data: existingList } = this.waitingRoomConversations.data ?? {}

        this.waitingRoomConversations = updateState(
          this.waitingRoomConversations,
          new ConversationsIndexRO(
            setNewConversations(payload?.page ?? 1, existingList ?? [], data)
          )
        )
        this.updateWaitingRoomAttention()
      } catch (error) {
        this.waitingRoomConversations = updateState(
          this.waitingRoomConversations,
          error as Error
        )
        throw error
      }
    },

    async fetchInvitedConversations(payload: {
      page: number
      chatWidgetId: number
    }) {
      if (isNaN(payload?.chatWidgetId)) {
        return
      }
      if (isPending(this.invitedConversations)) {
        conversationsControllers.invited?.abortFetchRequest()
      }
      conversationsControllers.invited = new AbortFetchRequest()

      if (payload.page === 1) {
        this.invitedConversations = updateState(
          this.invitedConversations,
          new ConversationsIndexRO({
            data: [],
          })
        )
      }
      this.invitedConversations = updateState(this.invitedConversations)

      try {
        const dto = new ConversationsIndexDTO({
          ...payload,
          perPage: 20,
          tab: this.selectedTab,
          subTab: ChatSubTabs.Invited,
          search: this.searchQuery,
        })

        const data = await apiTransport.get<
          ConversationsIndexDTO,
          ConversationsIndexRO
        >(
          '/conversations',
          dto,
          ConversationsIndexRO,
          conversationsControllers.invited.getInstance().signal
        )

        const { data: existingList } = this.invitedConversations.data ?? {}

        this.invitedConversations = updateState(
          this.invitedConversations,
          new ConversationsIndexRO(
            setNewConversations(payload?.page ?? 1, existingList ?? [], data)
          )
        )
      } catch (error) {
        this.invitedConversations = updateState(
          this.invitedConversations,
          error as Error
        )
        throw error
      }
    },

    resetConversationState() {
      this.conversation = getInitialState()
    },

    async fetchAndSetActiveConversation(dto: { conversationId: number }) {
      if (
        this.currentConversationId &&
        this.currentConversationId !== dto.conversationId
      ) {
        return
      }

      this.conversation = updateState(this.conversation)
      try {
        const conversation = await this.fetchConversation(dto)

        const data =
          !this.currentConversationId ||
          this.currentConversationId === conversation?.id
            ? conversation
            : this.conversation.data

        this.conversation = updateState(this.conversation, data)

        return conversation
      } catch (error) {
        this.conversation = updateState(this.conversation, error as Error)
        throw error
      }
    },

    refetchActiveConversation() {
      const conversationId = this.currentConversationId
      if (conversationId) {
        return this.fetchAndSetActiveConversation({ conversationId })
      }
    },

    async fetchAndReplaceConversationById(dto: { conversationId: number }) {
      const conversation = await this.fetchConversation(dto)
      if (!conversation) {
        return
      }
      // replace active conversation if needed
      if (this.conversation.data?.id === conversation.id) {
        this.conversation = updateState(this.conversation, conversation)
      }
      // replace conversation if it exists in openedConversations or waitingRoomConversations
      this.replaceConversation(conversation, ConversationSubTabs.Opened)
      this.replaceConversation(conversation, ConversationSubTabs.Waiting)
    },

    async fetchConversation(dto: { conversationId: number }) {
      const messagesStore = useMessagesStore()
      messagesStore.resetSendMessageState()

      const { data } = (await apiTransport.get(
        `/conversations/${dto.conversationId}`,
        new ConversationShowDTO(dto),
        ConversationItemRO
      )) ?? { data: {} }

      return data
    },

    async fetchChatUsers(dto: {
      chatWidgetId: number
      page?: number
      perPage?: number
      nameFilter?: string
    }) {
      this.users = updateState(this.users)

      try {
        const { data, meta } = (await apiTransport.get<
          ChatUsersIndexDTO,
          ChatUsersIndexRO
        >(
          '/chat_users',
          new ChatUsersIndexDTO({ ...dto, typeFilter: [UserType.USER] }),
          ChatUsersIndexRO
        )) ?? { data: [], meta: {} }

        const { data: existingList = [] } = this.users.data ?? {}

        const dataToSet =
          dto.page === 1
            ? { data, meta }
            : {
                meta,
                data: [...existingList, ...data],
              }
        this.users = updateState(this.users, new ChatUsersIndexRO(dataToSet))
      } catch (error) {
        this.users = updateState(this.users, error as Error)
        throw error
      }
    },

    async fetchChatBots(dto: {
      chatWidgetId: number
      page?: number
      perPage?: number
      nameFilter?: string
    }) {
      this.bots = updateState(this.bots)

      try {
        const { data, meta } = await apiTransport.get(
          '/chat_users',
          new ChatUsersIndexDTO({ ...dto, typeFilter: [UserType.BOT] }),
          ChatUsersIndexRO
        )

        const { data: existingList = [] } = this.bots.data ?? {}

        const dataToSet =
          dto.page === 1
            ? { data, meta }
            : {
                meta,
                data: [...existingList, ...data],
              }
        this.bots = updateState(this.bots, new ChatUsersIndexRO(dataToSet))
      } catch (error) {
        this.bots = updateState(this.bots, error as Error)
        throw error
      }
    },

    async createConversation(payload: { data: ICreateConversation }) {
      const { data } = (await apiTransport.post<
        CreateConversationDTO,
        ConversationItemRO
      >(
        '/conversations',
        new CreateConversationDTO(payload),
        CreateConversationDTO,
        ConversationItemRO
      )) ?? { data: {} }

      return data
    },

    async updateConversation(payload: IUpdateDataDTO) {
      const { data } = (await apiTransport.put(
        `/conversations/${payload.conversationId}`,
        new ConversationUpdateDTO(payload),
        ConversationUpdateDTO,
        ConversationItemRO
      )) ?? { data: {} }

      return data
    },

    setSelectedTab(tab: ChatTabs) {
      this.selectedTab = tab
      lsSave(LsGlobalKeys.SelectedTab, tab)
    },

    setSelectedSubTab(tab: ChatSubTabs) {
      if (tab === ChatSubTabs.Waiting && this.selectedTab !== ChatTabs.Closed) {
        this.waitingRoomAttention[this.selectedTab] = false
        this.markWaitingRoomAttentionNoticed()
        this.updateAllTabAttention()
      }
      this.selectedSubTab = tab
    },

    updateWaitingRoomAttention(maybeAllTabMsg?: Message) {
      // if this.waitingRoomAttention[this.selectedTab] is true we need trrigger again for applying styles to the waiting tab
      if (
        maybeAllTabMsg?.messageType === MessageType.WAITING_BOT_FINISHED &&
        this.selectedTab !== ChatTabs.Closed &&
        this.waitingRoomAttention[this.selectedTab]
      ) {
        this.waitingRoomAttention[this.selectedTab] = false
      }
      setTimeout(() => {
        // If it's Closed tab then there is no Waiting room messages and we should check only All tab attention marker
        if (this.selectedTab === ChatTabs.Closed) {
          this.updateAllTabAttention(maybeAllTabMsg)
          return
        }

        // If Waiting sub tab is opened we should mark its messages as attended.
        // Otherwise it needs to check if there is WAITING_BOT_FINISHED message that has not been visited yet.
        if (this.selectedSubTab === ChatSubTabs.Waiting) {
          this.markWaitingRoomAttentionNoticed()
          this.waitingRoomAttention[this.selectedTab] = false
        } else {
          const conversations = this.waitingRoomConversations.data?.data || []
          const needAttention = conversations.some(
            ({ lastMessage, assignee }) => {
              return (
                lastMessage?.messageType === MessageType.WAITING_BOT_FINISHED &&
                assignee?.type === UserType.BOT &&
                !this.waitingRoomAttentionNoticedIds[lastMessage.id]
              )
            }
          )

          this.waitingRoomAttention[this.selectedTab] = needAttention
        }

        // After processing Waiting tab messages it needs to show/hide All tab attention marker

        this.updateAllTabAttention(maybeAllTabMsg)
      })
    },

    updateAllTabAttention(maybeAllTabMsg?: Message) {
      const conversations = this.waitingRoomConversations.data?.data || []

      if (this.selectedTab === ChatTabs.All && this.allTabAttention) {
        this.allTabAttention = this.waitingRoomAttention[ChatTabs.All]
      } else if (
        maybeAllTabMsg &&
        !this.waitingRoomAttentionNoticedIds[maybeAllTabMsg.id] &&
        !conversations.some(({ id }) => id === maybeAllTabMsg.conversationId)
      ) {
        this.allTabAttention = true
      }
    },

    markWaitingRoomAttentionNoticed() {
      const conversations = this.waitingRoomConversations.data?.data || []
      this.waitingRoomAttentionNoticedIds = conversations.reduce(
        (acc, { lastMessage, assignee }) => {
          if (
            lastMessage?.messageType === MessageType.WAITING_BOT_FINISHED &&
            assignee?.type === UserType.BOT
          ) {
            return { ...acc, [lastMessage.id]: true }
          }
          return acc
        },
        this.waitingRoomAttentionNoticedIds
      )
    },

    addConversation(
      conversation: Conversation,
      destination: keyof ConversationsDataTypes
    ) {
      const { data, meta } = this[destination].data ?? {}

      if (!data || !meta) {
        return
      }

      const existingIndex = data.findIndex(({ id }) => id === conversation.id)
      if (existingIndex !== -1 && this[destination].data?.data[existingIndex]) {
        this.updateConversationList(conversation, existingIndex, destination)
        return
      }

      this[destination] = updateState(
        this[destination],
        new ConversationsIndexRO({
          meta: {
            ...meta,
            totalCount: meta.totalCount + 1,
          },
          data: [conversation, ...data],
        })
      )
    },

    updateCurrentConversation(conversation: Conversation) {
      if (this.currentConversationId === conversation.id) {
        this.conversation = updateState(this.conversation, conversation)
      }
    },

    updateConversationList(
      conversation: Conversation,
      existingIndex: number,
      destination: keyof ConversationsDataTypes
    ) {
      const clonedData = this[destination].data?.data as Conversation[]
      if (clonedData[existingIndex]) {
        const { meta } = this[destination].data ?? {}
        if (!meta) {
          return
        }
        clonedData[existingIndex] = conversation
        this[destination] = updateState(
          this[destination],
          new ConversationsIndexRO({
            meta: {
              ...meta,
            },
            data: [...clonedData],
          })
        )
        this.updateCurrentConversation(conversation)
      }
    },

    removeConversation(id: Conversation['id']) {
      const conversationsData: Array<keyof ConversationsDataTypes> = [
        ConversationSubTabs.Opened,
        ConversationSubTabs.Waiting,
        ConversationSubTabs.Invited,
      ]

      conversationsData.forEach((conversations) => {
        const { data, meta } = this[conversations].data ?? {}
        if (!data || !meta) {
          return
        }

        const updatedData = data?.filter((item) => item.id !== id) ?? []

        this[conversations] = updateState(
          this[conversations],
          new ConversationsIndexRO({
            meta: {
              ...meta,
              totalCount:
                updatedData.length !== data?.length
                  ? meta.totalCount - 1
                  : meta.totalCount,
            },
            data: updatedData,
          })
        )
      })
    },

    replaceConversation(
      newConversation: IConversation,
      destination: keyof ConversationsDataTypes
    ) {
      const { data, meta } = this[destination].data ?? {}

      this[destination] = updateState(
        this[destination],
        new ConversationsIndexRO({
          meta,
          data:
            data?.map((item) =>
              item.id === newConversation.id ? newConversation : item
            ) ?? [],
        })
      )
    },

    markConversationLastMessageRead(messageId?: number, readTime?: string) {
      if (!messageId || !readTime) {
        return
      }

      const conversation = [
        ...(this.openedConversations?.data?.data || []),
        ...(this.waitingRoomConversations?.data?.data || []),
      ].find(
        (item) => item.lastMessage?.id && item.lastMessage.id === messageId
      )

      if (conversation && conversation.lastMessage?.actualStatus) {
        conversation.lastMessage.actualStatus.readTime = readTime
        this.replaceConversation(
          conversation,
          this.getSubTabByStatus(conversation)
        )
      }
    },

    getSubTabByStatus(conversation: IConversation) {
      const dataVariants: Record<number, keyof ConversationsDataTypes> = {
        [ConversationStatus.OPEN]: ConversationSubTabs.Opened,
        [ConversationStatus.INVITED]: ConversationSubTabs.Invited,
        [ConversationStatus.ASSIGNED_TO_BOT]: ConversationSubTabs.Waiting,
      }

      return dataVariants[conversation.status]
    },
  },

  getters: {
    firstOpenedConversation(state) {
      return state.openedConversations.data?.data?.[0] ?? null
    },

    getFirstConversationInList(state) {
      return (
        listType: keyof ConversationsDataTypes,
        options?: { except?: Conversation['id'] }
      ) => {
        const first = state[listType]?.data?.data?.[0]
        const second = state[listType]?.data?.data?.[1]
        return options?.except && options?.except === first?.id ? second : first
      }
    },

    getConversation(state) {
      return (id: Conversation['id']) => {
        const { data: opened = [] } = state.openedConversations.data ?? {}
        const { data: waiting = [] } = state.waitingRoomConversations.data ?? {}
        const { data: invited = [] } = state.invitedConversations.data ?? {}

        return [...opened, ...waiting, ...invited].find(
          (item) => item.id === id
        )
      }
    },

    hasWaitingRoomAttention(state) {
      return (
        state.selectedTab !== ChatTabs.Closed &&
        !!state.waitingRoomAttention[state.selectedTab]
      )
    },
  },
})
